import { useActionState, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { setPassword, type SetPasswordState } from "@/api/auth";
import { Button } from "@/components/ui/Button";
import { useTranslations } from "@/lib/useTranslations";

const initialState: SetPasswordState = {};

const inputClassName =
  "w-full wireframe-border bg-whart-bg px-3 py-2 text-sm text-whart-text outline-none focus:border-whart-dark disabled:opacity-50";

function EyeIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
      <line x1="2" x2="22" y1="2" y2="22" />
    </svg>
  );
}

function PasswordField({
  id,
  name,
  label,
  autoComplete,
  disabled,
  showPassword,
  onToggleVisibility,
  toggleLabel,
}: {
  id: string;
  name: string;
  label: string;
  autoComplete: string;
  disabled: boolean;
  showPassword: boolean;
  onToggleVisibility: () => void;
  toggleLabel: string;
}) {
  return (
    <div className="space-y-2">
      <label htmlFor={id} className="text-sm font-medium text-whart-text">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          name={name}
          type={showPassword ? "text" : "password"}
          autoComplete={autoComplete}
          required
          minLength={6}
          disabled={disabled}
          className={`${inputClassName} pr-10`}
        />
        <button
          type="button"
          onClick={onToggleVisibility}
          disabled={disabled}
          className="absolute inset-y-0 right-0 flex items-center px-3 text-neutral-400 transition-colors hover:text-whart-text disabled:opacity-50"
          aria-label={toggleLabel}
        >
          {showPassword ? (
            <EyeOffIcon className="size-4" />
          ) : (
            <EyeIcon className="size-4" />
          )}
        </button>
      </div>
    </div>
  );
}

export function SetPasswordForm() {
  const t = useTranslations("setPassword");
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [state, formAction, isPending] = useActionState(setPassword, initialState);

  useEffect(() => {
    if (state.redirectTo) {
      navigate(state.redirectTo);
    }
  }, [state.redirectTo, navigate]);

  return (
    <form action={formAction} noValidate className="space-y-4">
      <p className="rounded-xl border border-black/10 bg-zinc-50 px-3 py-2 text-sm text-neutral-700">
        {t("notice")}
      </p>

      <PasswordField
        id="password"
        name="password"
        label={t("password")}
        autoComplete="new-password"
        disabled={isPending}
        showPassword={showPassword}
        onToggleVisibility={() => setShowPassword((visible) => !visible)}
        toggleLabel={showPassword ? t("hidePassword") : t("showPassword")}
      />

      <PasswordField
        id="confirmPassword"
        name="confirmPassword"
        label={t("confirmPassword")}
        autoComplete="new-password"
        disabled={isPending}
        showPassword={showConfirmPassword}
        onToggleVisibility={() => setShowConfirmPassword((visible) => !visible)}
        toggleLabel={
          showConfirmPassword ? t("hidePassword") : t("showPassword")
        }
      />

      {state.error ? (
        <p
          role="alert"
          className="wireframe-border px-3 py-2 text-sm text-whart-urgent"
        >
          {state.error}
        </p>
      ) : null}

      <Button type="submit" className="w-full" disabled={isPending}>
        {isPending ? t("saving") : t("submit")}
      </Button>
    </form>
  );
}
